import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { NavBar } from '../organisms/NavBar';
import { BottomNav } from '../organisms/BottomNav';
import { CartFab } from '../organisms/CartFab';
import { ActiveOrderBanner } from '../organisms/ActiveOrderBanner';
import { useDetectLocation } from '../../hooks/useDetectLocation';
import { useWebPush } from '../../hooks/useWebPush';
import { useSocket } from '../../hooks/useSocket';
import { useThemeStore } from '../../store/theme.store';
import { useCartStore } from '../../features/cart/store/cart.store';
import { useAuthStore } from '../../store/auth.store';
import { ordersApi, paymentsApi } from '@grandxl/api-client';
import { PaymentStatus } from '@grandxl/types';
// Recover from abandoned Paystack sessions. Paystack keeps payment status as
// "pending" for minutes after the user cancels — so polling Paystack is useless.
// Instead we check the ORDER's own payment record: if payment.status is COMPLETED
// (webhook arrived while user was away), clear cart. Otherwise leave the order
// alone (do NOT cancel — 30-min server timeout catches truly abandoned orders,
// and cancelling here could nuke an order about to succeed).
//
// Fires on: app mount / auth flip / tab focus / network reconnect. That way if the
// user closes the tab mid-payment then reopens the site 45s later, we detect the
// completed webhook and finish the cart-clear + navigation instead of leaving them
// with a stale cart.
function usePaystackRecovery() {
    const location = useLocation();
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
    const clearCart = useCartStore((s) => s.clearCart);
    useEffect(() => {
        // The callback page handles its own cleanup
        if (location.pathname === '/payment/callback')
            return;
        if (!isAuthenticated)
            return;
        function checkPending() {
            const orderId = sessionStorage.getItem('pendingPaystackOrderId');
            const reference = sessionStorage.getItem('pendingPaystackReference');
            // Order recovery — reads status; if the webhook landed and marked
            // the order COMPLETED, cart is cleared and markers reaped. If still
            // PENDING, actively verify (forces server to query Paystack directly)
            // — the customer paid in the inline popup, the server just hasn't been
            // told yet. Without this call a webhook-missing deployment leaves the
            // order PENDING until the 30-min auto-cancel, by which time the
            // customer has paid for nothing.
            if (orderId) {
                ordersApi.getById(orderId)
                    .then((res) => {
                    const order = res.data.data;
                    if (order.payment.status === PaymentStatus.COMPLETED) {
                        clearCart();
                        sessionStorage.removeItem('pendingPaystackOrderId');
                        sessionStorage.removeItem('pendingPaystackReference');
                        return;
                    }
                    if (reference && order.payment.status === PaymentStatus.PENDING) {
                        paymentsApi.verify(reference).catch(() => undefined);
                    }
                })
                    .catch(() => undefined);
            }
            // Reference-only recovery — wallet top-ups (no orderId). Hitting
            // verify forces the server to query Paystack directly and run
            // handleChargeSuccess if the charge resolved. Idempotent on repeat
            // calls via the atomic PENDING→COMPLETED flip filter.
            if (reference && !orderId) {
                paymentsApi.verify(reference)
                    .then((res) => {
                    if (res.data.data.verified) {
                        sessionStorage.removeItem('pendingPaystackReference');
                    }
                })
                    .catch(() => undefined);
            }
        }
        // Initial check
        checkPending();
        // Re-check on tab focus (user returns to tab)
        function onFocus() { checkPending(); }
        // Re-check on network reconnect (offline → online)
        function onOnline() { checkPending(); }
        window.addEventListener('focus', onFocus);
        window.addEventListener('online', onOnline);
        return () => {
            window.removeEventListener('focus', onFocus);
            window.removeEventListener('online', onOnline);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated]);
}
export function AppShell() {
    useDetectLocation();
    useWebPush();
    useSocket();
    usePaystackRecovery();
    const isDark = useThemeStore((s) => s.isDark);
    useEffect(() => {
        document.documentElement.classList.toggle('dark', isDark);
    }, [isDark]);
    return (_jsxs("div", { className: "min-h-screen bg-white dark:bg-zinc-950 text-gray-900 dark:text-gray-100", children: [_jsx(NavBar, {}), _jsx("main", { className: "pt-0 md:pt-16 pb-20 md:pb-0", children: _jsx(Outlet, {}) }), _jsx(CartFab, {}), _jsx(ActiveOrderBanner, {}), _jsx(BottomNav, {})] }));
}
//# sourceMappingURL=AppShell.js.map